/**
 * Motion engine for the Higher Education theme.
 *
 * Lenis version: 1.1.20 (vendored at assets/lenis.min.js)
 *   Source: https://cdn.jsdelivr.net/npm/lenis@1.1.20/dist/lenis.min.js
 *
 * Design:
 *  - One shared rAF tick, one IntersectionObserver per concern.
 *  - Motion is gated globally on `motionEnabled`: disabled in Shopify
 *    designMode and when the user prefers reduced motion.
 *  - Sections opt in declaratively via `data-he-reveal`, `data-he-parallax`,
 *    and `data-he-pin` attributes. `autoInit()` wires them up.
 *  - Re-runs on Shopify's `shopify:section:load` event so sections added
 *    in the theme editor get wired without reloading the page.
 *  - Mobile (<=749px) never pins and never runs Lenis — native scroll only.
 */

const MOBILE_MQ = '(max-width: 749px)';
const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';

const isDesignMode = () => !!(typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode);
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia(REDUCED_MOTION_MQ).matches;

export const isMobile = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia(MOBILE_MQ).matches;

export const motionEnabled = !isDesignMode() && !prefersReducedMotion();

// ---------------------------------------------------------------------------
// Shared rAF tick
// ---------------------------------------------------------------------------

const tickSubscribers = new Set();
let tickRunning = false;

function startTick() {
  if (tickRunning) return;
  tickRunning = true;
  const loop = (time) => {
    if (!tickRunning) return;
    for (const fn of tickSubscribers) {
      try {
        fn(time);
      } catch (err) {
        // Never let one subscriber kill the loop
        console.error('[motion] tick subscriber error:', err);
      }
    }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

function subscribeTick(fn) {
  tickSubscribers.add(fn);
  startTick();
  return () => tickSubscribers.delete(fn);
}

// ---------------------------------------------------------------------------
// Smooth scroll (Lenis)
// ---------------------------------------------------------------------------

let lenisInstance = null;
let lenisLoadPromise = null;

async function loadLenisScript() {
  if (window.Lenis) return window.Lenis;
  if (lenisLoadPromise) return lenisLoadPromise;

  const url = window.HE_LENIS_URL;
  if (!url) {
    console.warn('[motion] window.HE_LENIS_URL not set; Lenis will not load');
    return null;
  }

  lenisLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve(window.Lenis || null);
    script.onerror = () => reject(new Error('Failed to load Lenis from ' + url));
    document.head.appendChild(script);
  });

  try {
    return await lenisLoadPromise;
  } catch (err) {
    console.warn('[motion] Lenis load failed:', err);
    lenisLoadPromise = null;
    return null;
  }
}

export async function initSmoothScroll() {
  if (lenisInstance) return lenisInstance;
  if (!motionEnabled) return null;
  if (isMobile()) return null;

  const Lenis = await loadLenisScript();
  if (!Lenis) return null;

  lenisInstance = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
    smoothTouch: false,
    wheelMultiplier: 1,
    touchMultiplier: 1,
    // Defer scroll events that originate inside anything with data-lenis-prevent,
    // inside any element with native inner scroll (overflow auto/scroll), or inside
    // any currently-open <dialog>. Lets the cart drawer, search modal, header drawer,
    // and any future dialog scroll natively with the mouse wheel.
    prevent: (node) => {
      if (!node || node.nodeType !== 1) return false;
      if (node.closest('[data-lenis-prevent]')) return true;
      if (node.closest('dialog[open]')) return true;
      return false;
    },
  });

  let lastY = 0;
  subscribeTick((time) => {
    lenisInstance.raf(time);
    const y = lenisInstance.scroll;
    const velocity = y - lastY;
    lastY = y;
    window.dispatchEvent(
      new CustomEvent('he:scroll', {
        detail: {
          y,
          velocity,
          direction: velocity > 0 ? 1 : velocity < 0 ? -1 : 0,
        },
      })
    );
    // Expose velocity as a CSS var for velocity-driven effects
    document.documentElement.style.setProperty('--he-scroll-velocity', String(velocity));
  });

  // Pause Lenis entirely while any <dialog> is open — prevents the background
  // page from wiggling when the user scrolls inside the drawer, and ensures
  // touch/wheel events never race between Lenis and the dialog.
  if (typeof MutationObserver !== 'undefined') {
    const syncLenisWithDialogs = () => {
      const anyOpen = !!document.querySelector('dialog[open]');
      if (anyOpen) lenisInstance.stop();
      else lenisInstance.start();
    };
    const dialogObserver = new MutationObserver(syncLenisWithDialogs);
    dialogObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
    syncLenisWithDialogs();
  }

  // Debug hook
  window.__lenis = lenisInstance;
  return lenisInstance;
}

// ---------------------------------------------------------------------------
// Reveal-on-enter
// ---------------------------------------------------------------------------

const revealObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      )
    : null;

function splitCharacters(el) {
  // Only split once
  if (el.dataset.heCharSplit === '1') return;
  const text = el.textContent || '';
  el.dataset.heCharSplit = '1';
  const frag = document.createDocumentFragment();
  const lang = (document.documentElement.lang || 'en').toLowerCase();
  const safeLangs = ['en', 'es', 'fr', 'de', 'nl', 'pt', 'it'];
  const langOk = safeLangs.some((code) => lang.startsWith(code));
  if (!langOk) {
    // Fall back to word-level split on ambiguous scripts
    el.classList.add('he-reveal--slide-up');
    return;
  }
  // Split into words first so the browser can't break a word across lines.
  // Each word is wrapped in <span class="he-reveal-word"> with white-space: nowrap;
  // each character within a word is its own <span class="he-reveal-char"> for staggered animation.
  const words = text.split(/(\s+)/); // keeps whitespace tokens
  let index = 0;
  for (const token of words) {
    if (token.length === 0) continue;
    if (/^\s+$/.test(token)) {
      frag.appendChild(document.createTextNode(token));
      continue;
    }
    const wordSpan = document.createElement('span');
    wordSpan.className = 'he-reveal-word';
    for (const ch of token) {
      const charSpan = document.createElement('span');
      charSpan.className = 'he-reveal-char';
      charSpan.textContent = ch;
      charSpan.style.setProperty('--he-char-index', String(index));
      wordSpan.appendChild(charSpan);
      index++;
    }
    frag.appendChild(wordSpan);
  }
  el.textContent = '';
  el.appendChild(frag);
  el.style.setProperty('--he-char-count', String(index));
}

function setupReveal(el) {
  if (el.dataset.heRevealReady === '1') return;
  el.dataset.heRevealReady = '1';

  const variant = el.dataset.heReveal || 'fade';
  el.classList.add('he-reveal');
  el.classList.add('he-reveal--' + variant);

  const delay = parseInt(el.dataset.heDelay || '0', 10);
  const stagger = parseInt(el.dataset.heStagger || '0', 10);

  if (delay) el.style.setProperty('--he-reveal-delay', delay + 'ms');

  if (variant === 'char') {
    splitCharacters(el);
    if (stagger) el.style.setProperty('--he-reveal-stagger', stagger + 'ms');
  } else if (variant === 'stagger' && stagger) {
    // Stagger children
    const children = Array.from(el.children);
    children.forEach((child, i) => {
      child.classList.add('he-stagger-child');
      child.style.setProperty('--he-stagger-index', String(i));
    });
    el.style.setProperty('--he-reveal-stagger', stagger + 'ms');
  }

  if (!motionEnabled || !revealObserver) {
    el.classList.add('is-revealed');
    return;
  }

  revealObserver.observe(el);
}

export function reveal(el, opts = {}) {
  if (!el) return;
  if (opts.variant) el.dataset.heReveal = opts.variant;
  if (opts.delay != null) el.dataset.heDelay = String(opts.delay);
  if (opts.stagger != null) el.dataset.heStagger = String(opts.stagger);
  setupReveal(el);
}

// ---------------------------------------------------------------------------
// Parallax
// ---------------------------------------------------------------------------

const parallaxEntries = new Map(); // element -> { speed, axis, clamp, inView }

const parallaxObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const record = parallaxEntries.get(entry.target);
            if (record) record.inView = entry.isIntersecting;
          }
        },
        { threshold: 0, rootMargin: '20% 0px 20% 0px' }
      )
    : null;

let parallaxTickUnsubscribe = null;

function parallaxTick() {
  const vh = window.innerHeight || 1;
  for (const [el, record] of parallaxEntries) {
    if (!record.inView) continue;
    const rect = el.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    // progress from -1 (below viewport) through 0 (centered) to +1 (above viewport)
    const progress = (center - vh / 2) / (vh / 2 + rect.height / 2);
    let offset = -progress * record.speed * vh * 0.5;
    if (record.clamp) {
      const max = vh * 0.5 * Math.abs(record.speed);
      offset = Math.max(-max, Math.min(max, offset));
    }
    el.style.setProperty('--he-parallax-offset', offset.toFixed(2) + 'px');
  }
}

function ensureParallaxTick() {
  if (parallaxTickUnsubscribe) return;
  parallaxTickUnsubscribe = subscribeTick(parallaxTick);
}

function setupParallax(el) {
  if (el.dataset.heParallaxReady === '1') return;
  el.dataset.heParallaxReady = '1';

  const speed = parseFloat(el.dataset.heParallax || '0.3');
  const axis = el.dataset.heParallaxAxis || 'y';
  const clamp = el.dataset.heParallaxClamp !== '0';

  el.classList.add('he-parallax-layer');
  if (axis === 'x') el.classList.add('he-parallax-layer--x');

  if (!motionEnabled || !parallaxObserver || isMobile() && el.dataset.heParallaxMobile === '0') {
    return;
  }

  parallaxEntries.set(el, { speed, axis, clamp, inView: false });
  parallaxObserver.observe(el);
  ensureParallaxTick();
}

export function parallax(el, opts = {}) {
  if (!el) return;
  if (opts.speed != null) el.dataset.heParallax = String(opts.speed);
  if (opts.axis) el.dataset.heParallaxAxis = opts.axis;
  if (opts.clamp === false) el.dataset.heParallaxClamp = '0';
  setupParallax(el);
}

// ---------------------------------------------------------------------------
// Pin
// ---------------------------------------------------------------------------

const pinEntries = new Map(); // wrapper -> { inner, sentinel, duration, progress }
let pinTickUnsubscribe = null;

function pinTick() {
  for (const [wrapper, record] of pinEntries) {
    const rect = wrapper.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    if (total <= 0) {
      record.inner.style.setProperty('--he-pin-progress', '0');
      continue;
    }
    const scrolled = -rect.top;
    const progress = Math.max(0, Math.min(1, scrolled / total));
    if (progress !== record.progress) {
      record.progress = progress;
      record.inner.style.setProperty('--he-pin-progress', progress.toFixed(4));
    }
  }
}

function ensurePinTick() {
  if (pinTickUnsubscribe) return;
  pinTickUnsubscribe = subscribeTick(pinTick);
}

function setupPin(wrapper) {
  if (wrapper.dataset.hePinReady === '1') return;
  wrapper.dataset.hePinReady = '1';

  const desktopOnly = wrapper.dataset.hePinMobile !== '1';

  if (!motionEnabled || (desktopOnly && isMobile())) {
    wrapper.classList.add('he-pin-wrapper--disabled');
    return;
  }

  wrapper.classList.add('he-pin-wrapper');
  let inner = wrapper.querySelector('[data-he-pin-inner]');
  if (!inner) {
    // Fall back to first child
    inner = wrapper.firstElementChild;
    if (!inner) return;
  }
  inner.classList.add('he-pin-inner');

  pinEntries.set(wrapper, { inner, progress: -1 });
  ensurePinTick();
}

export function pin(wrapper, opts = {}) {
  if (!wrapper) return;
  if (opts.desktopOnly === false) wrapper.dataset.hePinMobile = '1';
  setupPin(wrapper);
}

// ---------------------------------------------------------------------------
// autoInit — scan the document (or subtree) and wire up declarative opt-ins
// ---------------------------------------------------------------------------

export function autoInit(root = document) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('[data-he-reveal]').forEach(setupReveal);
  scope.querySelectorAll('[data-he-parallax]').forEach(setupParallax);
  scope.querySelectorAll('[data-he-pin]').forEach(setupPin);
}

// Shopify theme editor: re-scan when a section is added
if (typeof document !== 'undefined') {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) autoInit(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    // Best-effort cleanup of observers for removed DOM
    if (!event.target) return;
    event.target.querySelectorAll('[data-he-reveal-ready]').forEach((el) => {
      if (revealObserver) revealObserver.unobserve(el);
    });
    event.target.querySelectorAll('[data-he-parallax-ready]').forEach((el) => {
      if (parallaxObserver) parallaxObserver.unobserve(el);
      parallaxEntries.delete(el);
    });
    event.target.querySelectorAll('[data-he-pin-ready]').forEach((el) => {
      pinEntries.delete(el);
    });
  });
}
